import { z } from 'zod';
import { geoFilterShape } from './geography.js';
import { paginationQuerySchema, queryBooleanSchema } from './pagination.js';
export const LOCATION_STATUSES = ['PENDING', 'SUGGESTED', 'VERIFIED'];
export const GEOFENCE_MIN_METERS = 25;
export const GEOFENCE_MAX_METERS = 5000;
export const DEFAULT_GEOFENCE_METERS = 250;
/** Trimmed, whitespace-collapsed, lower-cased; used for case-insensitive uniqueness. */
export function normalizeCatalogName(name) {
    return name.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en');
}
const catalogName = z
    .string()
    .transform((v) => v.normalize('NFC').trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(1, 'Name is required').max(120));
const catalogCode = z
    .string()
    .trim()
    .min(1)
    .max(30)
    .regex(/^[A-Za-z0-9._-]+$/, 'Code may contain letters, digits, dot, dash and underscore');
/** A zone or area name as typed in an import file: whitespace-collapsed; matched Arabic-aware (normalizeGeoName). */
const geoNameCell = z
    .string()
    .transform((v) => v.normalize('NFC').trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(1).max(80));
const latitude = z.number().min(-90).max(90);
const longitude = z.number().min(-180).max(180);
const geofence = z.number().int().min(GEOFENCE_MIN_METERS).max(GEOFENCE_MAX_METERS);
const bothOrNeither = (v) => (v.latitude == null) === (v.longitude == null);
const notNullIsland = (v) => !(v.latitude === 0 && v.longitude === 0);
export const brandDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    isActive: z.boolean(),
    branchCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createBrandRequestSchema = z.object({
    name: catalogName,
    code: catalogCode.nullable().default(null),
});
export const updateBrandRequestSchema = z
    .object({ name: catalogName, code: catalogCode.nullable(), isActive: z.boolean() })
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');
export const listBrandsQuerySchema = paginationQuerySchema.extend({
    search: z.string().trim().max(100).optional(),
    includeInactive: queryBooleanSchema.default(false),
});
export const branchDtoSchema = z.object({
    id: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    /** The area's Arabic name (kept under the old field name so older clients still show it). */
    area: z.string().nullable(),
    /** Zone -> Area -> Branch (D81). Null only for a branch created outside the API before its area is set. */
    areaId: z.string().nullable(),
    areaNameEn: z.string().nullable(),
    areaIsUnassigned: z.boolean(),
    zoneId: z.string().nullable(),
    zoneNameAr: z.string().nullable(),
    zoneNameEn: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    locationStatus: z.enum(LOCATION_STATUSES),
    geofenceRadiusMeters: z.number().int(),
    isActive: z.boolean(),
    pendingSuggestionCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createBranchRequestSchema = z
    .object({
    brandId: z.uuid(),
    name: catalogName,
    code: catalogCode.nullable().default(null),
    /** Null = the default zone's "غير محدد" area. */
    areaId: z.uuid().nullable().default(null),
    latitude: latitude.nullable().default(null),
    longitude: longitude.nullable().default(null),
    geofenceRadiusMeters: geofence.default(DEFAULT_GEOFENCE_METERS),
})
    .refine(bothOrNeither, { message: 'Provide both latitude and longitude, or neither', path: ['latitude'] })
    .refine(notNullIsland, { message: '0,0 is not a valid branch location', path: ['latitude'] });
export const updateBranchRequestSchema = z
    .object({
    name: catalogName,
    code: catalogCode.nullable(),
    areaId: z.uuid(),
    brandId: z.uuid(),
    geofenceRadiusMeters: geofence,
    isActive: z.boolean(),
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided');
/** Manual entry or map picker: sets the branch location and marks it VERIFIED. */
export const setBranchLocationRequestSchema = z
    .object({ latitude, longitude })
    .refine(notNullIsland, { message: '0,0 is not a valid branch location' });
export const listBranchesQuerySchema = paginationQuerySchema.extend({
    brandId: z.uuid().optional(),
    search: z.string().trim().max(100).optional(),
    ...geoFilterShape,
    locationStatus: z.enum(LOCATION_STATUSES).optional(),
    includeInactive: queryBooleanSchema.default(false),
});
export const SUGGESTION_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const locationSuggestionDtoSchema = z.object({
    id: z.string(),
    branchId: z.string(),
    branchName: z.string(),
    brandName: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    accuracyMeters: z.number().nullable(),
    status: z.enum(SUGGESTION_STATUSES),
    suggestedBy: z.object({ id: z.string(), fullName: z.string() }),
    visitSubmissionId: z.string().nullable(),
    createdAt: z.string(),
    reviewedAt: z.string().nullable(),
});
/** What field devices receive: only verified branches carry coarse (~110 m) coordinates. */
export const mobileCatalogResponseSchema = z.object({
    brands: z.array(z.object({ id: z.string(), name: z.string() })),
    branches: z.array(z.object({
        id: z.string(),
        brandId: z.string(),
        name: z.string(),
        code: z.string().nullable(),
        approxLatitude: z.number().nullable(),
        approxLongitude: z.number().nullable(),
    })),
    generatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// Bulk delete (merchants and branches)
// ---------------------------------------------------------------------------
/** Absolute ceiling per request; the server enforces `BULK_DELETE_MAX` (default 200) on top. */
export const BULK_DELETE_HARD_MAX = 1000;
export const bulkDeleteRequestSchema = z.object({
    ids: z.array(z.uuid()).min(1).max(BULK_DELETE_HARD_MAX),
    /** Plan only: returns exactly what would happen to each row, and writes nothing. */
    dryRun: z.boolean().default(false),
});
/**
 * Per-row result. The same rule as the single delete decides the outcome:
 * no history -> DELETED (hard); visits or alerts -> DEACTIVATED (soft, data kept).
 */
export const BULK_DELETE_OUTCOMES = ['DELETED', 'DEACTIVATED', 'SKIPPED', 'FAILED'];
/** Why a row ended as it did; the interface translates these, never the English `reason`. */
export const BULK_DELETE_REASON_CODES = [
    'HAS_HISTORY',
    'ACCOUNT_REMOVED',
    'NOT_FOUND',
    'OWN_ACCOUNT',
    'SUPER_ADMIN_ONLY',
    'LAST_SUPER_ADMIN',
    'UNEXPECTED',
];
export const bulkDeleteRowResultSchema = z.object({
    id: z.string(),
    /** Null when the id did not match anything. */
    name: z.string().nullable(),
    outcome: z.enum(BULK_DELETE_OUTCOMES),
    /** Machine-readable cause; the UI shows its translation. */
    reasonCode: z.enum(BULK_DELETE_REASON_CODES).nullable(),
    /** English explanation for logs and API clients; not shown in the interface. */
    reason: z.string().nullable(),
    /** Merchants only: active branches that go with it. */
    branchCount: z.number().int().nullable(),
});
export const bulkDeleteResponseSchema = z.object({
    dryRun: z.boolean(),
    results: z.array(bulkDeleteRowResultSchema),
    summary: z.object({
        deleted: z.number().int(),
        deactivated: z.number().int(),
        skipped: z.number().int(),
        failed: z.number().int(),
        /** Merchants only: branches removed or deactivated along with them. */
        branchesAffected: z.number().int(),
    }),
});
export function summarizeBulkDelete(results) {
    const count = (outcome) => results.filter((r) => r.outcome === outcome).length;
    return {
        deleted: count('DELETED'),
        deactivated: count('DEACTIVATED'),
        skipped: count('SKIPPED'),
        failed: count('FAILED'),
        branchesAffected: results
            .filter((r) => r.outcome === 'DELETED' || r.outcome === 'DEACTIVATED')
            .reduce((sum, r) => sum + (r.branchCount ?? 0), 0),
    };
}
// ---------------------------------------------------------------------------
// Bulk import
// ---------------------------------------------------------------------------
export const BULK_IMPORT_MAX_ROWS = 5000;
/** The importable columns, in template order. `rowNumber` is bookkeeping, not a column. */
const bulkImportRowShape = {
    rowNumber: z.number().int().min(1),
    brandName: catalogName,
    brandCode: catalogCode.nullable().default(null),
    branchName: catalogName,
    branchCode: catalogCode.nullable().default(null),
    zone: geoNameCell.nullable().default(null),
    area: geoNameCell.nullable().default(null),
    latitude: latitude.nullable().default(null),
    longitude: longitude.nullable().default(null),
    geofenceRadiusMeters: geofence.nullable().default(null),
};
export const bulkImportRowSchema = z
    .object(bulkImportRowShape)
    .refine(bothOrNeither, { message: 'Provide both latitude and longitude, or neither', path: ['latitude'] })
    .refine(notNullIsland, { message: '0,0 is not a valid branch location', path: ['latitude'] });
export const bulkImportRequestSchema = z.object({
    rows: z.array(bulkImportRowSchema).min(1).max(BULK_IMPORT_MAX_ROWS),
    dryRun: z.boolean().default(false),
    /** When false, rows matching an existing branch are SKIPPED instead of UPDATED. */
    updateExisting: z.boolean().default(true),
});
/** Server-side envelope: rows are validated one by one so every row gets its own outcome. */
export const bulkImportEnvelopeSchema = z.object({
    rows: z.array(z.unknown()).min(1).max(BULK_IMPORT_MAX_ROWS),
    dryRun: z.boolean().default(false),
    updateExisting: z.boolean().default(true),
});
export const BULK_ROW_OUTCOMES = ['CREATED', 'UPDATED', 'SKIPPED', 'ERROR'];
/**
 * Why a row ended as ERROR or SKIPPED: `reasonCode` on the row, with the values
 * to interpolate in `reasonParams`. The interface translates these; `message`
 * stays the English text for logs and older clients.
 */
export const BULK_IMPORT_ROW_REASONS = [
    /** The row failed validation. params.issues: [{ path, code, message }]. */
    'INVALID_ROW',
    /** A geofence radius was given without the setGeofenceRadius permission. */
    'GEOFENCE_NOT_PERMITTED',
    /** Same merchant + branch as an earlier row. params.row. */
    'DUPLICATE_ROW',
    /** The branch code is also used by an earlier row. params.code, params.row. */
    'DUPLICATE_BRANCH_CODE_IN_FILE',
    /** params.merchant */
    'MERCHANT_DELETED',
    /** A different merchant code was given for the same merchant earlier. params.code, params.otherCode, params.row. */
    'MERCHANT_CODE_CONFLICT_IN_FILE',
    /** One merchant code used for two different merchants in the file. params.code. */
    'MERCHANT_CODE_SHARED_IN_FILE',
    /** The merchant code belongs to another merchant. params.code, params.merchant. */
    'MERCHANT_CODE_TAKEN',
    /** The merchant already has a different code. params.code (the existing one). */
    'MERCHANT_HAS_OTHER_CODE',
    /** An area named without a zone exists in several zones. params.area, params.zones (joined names). */
    'AREA_AMBIGUOUS',
    /** params.branch */
    'BRANCH_DELETED',
    /** The branch code belongs to another branch. params.code. */
    'BRANCH_CODE_TAKEN',
    /** A concurrent change prevented the create; re-run the import. */
    'CONCURRENT_CHANGE',
    /** SKIPPED: the branch exists and updateExisting is false. */
    'BRANCH_EXISTS',
    /** SKIPPED: the row matches the existing branch exactly. */
    'NO_CHANGES',
];
export const bulkImportRowReasonSchema = z.enum(BULK_IMPORT_ROW_REASONS);
/** Values for a reason's translation. Strings and numbers only, except `issues` (INVALID_ROW). */
export const bulkImportReasonParamsSchema = z.object({
    row: z.number().int().optional(),
    code: z.string().optional(),
    otherCode: z.string().optional(),
    merchant: z.string().optional(),
    branch: z.string().optional(),
    area: z.string().optional(),
    zones: z.string().optional(),
    issues: z.array(z.object({ path: z.string(), code: z.string(), message: z.string() })).optional(),
});
export const bulkImportRowResultSchema = z.object({
    rowNumber: z.number().int(),
    outcome: z.enum(BULK_ROW_OUTCOMES),
    brandCreated: z.boolean(),
    /** English, for logs and older clients. */
    message: z.string().nullable(),
    /** Set on ERROR and SKIPPED rows (null on CREATED/UPDATED). Optional: older servers do not send it. */
    reasonCode: bulkImportRowReasonSchema.nullable().optional(),
    reasonParams: bulkImportReasonParamsSchema.optional(),
});
export const bulkImportResponseSchema = z.object({
    dryRun: z.boolean(),
    summary: z.object({
        totalRows: z.number().int(),
        createdBrands: z.number().int(),
        /** Zones and areas named in the file that did not exist yet (D81). Optional for older servers. */
        createdZones: z.number().int().optional(),
        createdAreas: z.number().int().optional(),
        createdBranches: z.number().int(),
        updatedBranches: z.number().int(),
        skipped: z.number().int(),
        errors: z.number().int(),
    }),
    rows: z.array(bulkImportRowResultSchema),
});
/** Columns whose schema entry accepts null are optional. */
const isOptional = (key) => bulkImportRowShape[key].safeParse(null).success;
const column = (spec) => ({ ...spec, required: !isOptional(spec.key) });
/**
 * Template order = this order. A test fails if this list stops matching the
 * row schema, so the template can never offer a column the importer ignores,
 * or omit one it needs.
 */
export const BULK_IMPORT_COLUMNS = [
    column({
        key: 'brandName',
        kind: 'text',
        header: { ar: 'اسم التاجر', en: 'Merchant name' },
        format: {
            ar: 'نص، حتى 120 حرف. لو التاجر موجود بنفس الاسم تتضاف الفروع له.',
            en: 'Text, up to 120 characters. An existing merchant with the same name gets the branches.',
        },
        aliases: ['merchant', 'التاجر', 'brand', 'brand name', 'brandname', 'العلامة التجارية', 'البراند', 'الماركة'],
    }),
    column({
        key: 'brandCode',
        kind: 'text',
        header: { ar: 'كود التاجر', en: 'Merchant code' },
        format: { ar: 'نص، حتى 30 حرف، مميز لكل تاجر. اختياري.', en: 'Text, up to 30 characters, unique per merchant. Optional.' },
        aliases: ['merchant code', 'brand code', 'brandcode'],
    }),
    column({
        key: 'branchName',
        kind: 'text',
        header: { ar: 'اسم الفرع', en: 'Branch name' },
        format: { ar: 'نص، حتى 120 حرف. مميز داخل نفس التاجر.', en: 'Text, up to 120 characters. Unique within the merchant.' },
        aliases: ['branch', 'branchname', 'الفرع'],
    }),
    column({
        key: 'branchCode',
        kind: 'text',
        header: { ar: 'كود الفرع', en: 'Branch code' },
        format: { ar: 'نص، حتى 30 حرف، مميز لكل فرع. اختياري.', en: 'Text, up to 30 characters, unique per branch. Optional.' },
        aliases: ['code', 'branchcode', 'الكود'],
    }),
    column({
        key: 'zone',
        kind: 'text',
        header: { ar: 'القطاع', en: 'Zone' },
        format: {
            ar: 'اسم أو كود القطاع (مثلاً: القاهرة). اختياري. لو مش موجود بيتعمل. المطابقة بتتجاهل المسافات وحالة الحروف والهمزات.',
            en: 'Zone name or code (e.g. Cairo). Optional; created if missing. Matched ignoring spaces, case and Arabic letter variants.',
        },
        aliases: ['region', 'sector', 'zone name', 'القطاع الجغرافي', 'الإقليم'],
    }),
    column({
        key: 'area',
        kind: 'text',
        header: { ar: 'المنطقة', en: 'Area' },
        format: {
            ar: 'اسم أو كود المنطقة داخل القطاع (مثلاً: مدينة نصر). اختياري. لو مش موجودة بتتعمل. فاضي = "غير محدد".',
            en: 'Area name or code within the zone (e.g. Nasr City). Optional; created if missing. Empty = "Unassigned".',
        },
        aliases: ['district', 'الحي', 'المنطقه', 'area name'],
    }),
    column({
        key: 'latitude',
        kind: 'number',
        header: { ar: 'خط العرض', en: 'Latitude' },
        format: {
            ar: 'رقم عشري من -90 لـ 90. سيبه فاضي لو مش معروف، ومعاه خط الطول.',
            en: 'Decimal from -90 to 90. Leave empty if unknown, together with longitude.',
        },
        aliases: ['lat'],
    }),
    column({
        key: 'longitude',
        kind: 'number',
        header: { ar: 'خط الطول', en: 'Longitude' },
        format: {
            ar: 'رقم عشري من -180 لـ 180. لازم مع خط العرض أو الاتنين فاضيين.',
            en: 'Decimal from -180 to 180. Give both coordinates or neither.',
        },
        aliases: ['lng', 'lon', 'long'],
    }),
    column({
        key: 'geofenceRadiusMeters',
        kind: 'number',
        header: { ar: 'نطاق الفرع (متر)', en: 'Geofence radius (m)' },
        format: {
            ar: `رقم صحيح من ${GEOFENCE_MIN_METERS} لـ ${GEOFENCE_MAX_METERS}. فاضي = ${DEFAULT_GEOFENCE_METERS} متر.`,
            en: `Whole number from ${GEOFENCE_MIN_METERS} to ${GEOFENCE_MAX_METERS}. Empty = ${DEFAULT_GEOFENCE_METERS} m.`,
        },
        aliases: ['geofence', 'radius', 'geofence radius', 'geofenceradiusmeters', 'نطاق', 'نصف القطر', 'النطاق'],
    }),
];
/** Example rows for the template: one Arabic name, and one branch without coordinates to show that is allowed. */
export const BULK_IMPORT_EXAMPLE_ROWS = [
    {
        brandName: 'سوبر ماركت النور',
        brandCode: 'NOUR',
        branchName: 'فرع مدينة نصر',
        branchCode: 'NOUR-001',
        zone: 'القاهرة',
        area: 'مدينة نصر',
        latitude: 30.0561,
        longitude: 31.33,
        geofenceRadiusMeters: 200,
    },
    {
        brandName: 'Metro Markets',
        brandCode: 'METRO',
        branchName: 'Maadi',
        branchCode: 'MET-014',
        zone: 'القاهرة',
        area: 'Maadi',
        latitude: 29.9602,
        longitude: 31.2569,
        geofenceRadiusMeters: null,
    },
    {
        brandName: 'سوبر ماركت النور',
        brandCode: 'NOUR',
        branchName: 'فرع المعادي',
        branchCode: null,
        zone: null,
        area: null,
        latitude: null,
        longitude: null,
        geofenceRadiusMeters: null,
    },
];
/** Normalises a header cell for matching: trimmed, case-folded, `_` and runs of spaces collapsed. */
export function normalizeImportHeader(value) {
    return String(value ?? '')
        .normalize('NFC')
        .trim()
        .toLocaleLowerCase('en')
        .replace(/[_\s]+/g, ' ');
}
/** Every header accepted for a column: both template headers plus the legacy aliases. */
export function acceptedImportHeaders(spec) {
    return [spec.header.ar, spec.header.en, ...spec.aliases].map(normalizeImportHeader);
}
/** @deprecated kept for callers of the old name; derived from BULK_IMPORT_COLUMNS. */
export const BULK_IMPORT_HEADER_ALIASES = Object.fromEntries(BULK_IMPORT_COLUMNS.map((spec) => [spec.key, acceptedImportHeaders(spec)]));
//# sourceMappingURL=catalog.js.map